import useApiRequest from "@/lib/hooks/use-request";
import { UserSelectableEntityResponseDto } from "@/lib/interfaces/users";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import LinkedItemManager from "@/ui-components/ui/linked-item-manager";
import { useCallback } from "react";

type UserLinkedProps = {
  onAssign: (userId: string, title: string) => Promise<void>;
} & (
  | {
      multiple: true;
      item: UserSelectableEntityResponseDto[];
      onRemove: (id: string) => Promise<void>;
    }
  | {
      multiple?: false;
      item?: UserSelectableEntityResponseDto;
      onRemove?: () => Promise<void>;
    }
);

const UserLinked = ({ item, onAssign, onRemove, multiple = false }: UserLinkedProps) => {
  const { apiRequest } = useApiRequest();

  const fetchSearchResults = useCallback(
    async (pagination: Pagination, searchText: string): Promise<Paginated<UserSelectableEntityResponseDto>> => {
      try {
        let url = `settings/users/search?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
        if (searchText) {
          url = `${url}&search=${searchText}`;
        }
        const response = await apiRequest<Paginated<UserSelectableEntityResponseDto>>(url, "GET");

        if (!response.data) {
          return {
            docs: [],
            hasNextPage: false,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
            page: pagination.pageIndex,
            hasPrevPage: pagination.pageIndex > 0,
            count: 0,
            pageCount: 0,
          };
        }

        return response.data;
      } catch (error) {
        console.error("Error fetching users:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [apiRequest]
  );

  const renderSearchOption = useCallback(
    (user: UserSelectableEntityResponseDto) => ({
      label: user.salesRankTitle ? `${user.value} - ${user.salesRankTitle}` : user.value,
      value: user._id,
    }),
    []
  );

  if (multiple) {
    return (
      <>
        {Array.isArray(item) &&
          item.length > 0 &&
          item.map((user) => (
            <div key={user._id}>
              <LinkedItemManager<UserSelectableEntityResponseDto>
                fetchSearchResults={fetchSearchResults}
                renderSearchOption={renderSearchOption}
                customOnSubmit={onAssign}
                removeItem={onRemove}
                link={{
                  title: user.salesRankTitle ? `${user.value} - ${user.salesRankTitle}` : user.value,
                  href: `/settings/users/${user._id}`,
                  id: user._id,
                }}
                title="Benutzer"
              />
            </div>
          ))}
        <div>
          <LinkedItemManager<UserSelectableEntityResponseDto>
            fetchSearchResults={fetchSearchResults}
            renderSearchOption={renderSearchOption}
            customOnSubmit={onAssign}
            removeItem={onRemove}
            title="Benutzer"
            multiple={true}
          />
        </div>
      </>
    );
  }

  return (
    <LinkedItemManager<UserSelectableEntityResponseDto>
      fetchSearchResults={fetchSearchResults}
      renderSearchOption={renderSearchOption}
      customOnSubmit={onAssign}
      removeItem={onRemove}
      link={
        item && !Array.isArray(item)
          ? {
              title: item.salesRankTitle ? `${item.value} - ${item.salesRankTitle}` : item.value,
              href: `/settings/users/${item._id}`,
              id: item._id,
            }
          : undefined
      }
      title="Person"
    />
  );
};

export default UserLinked;
